import { ClickAwayListener, Typography, useMediaQuery } from "@mui/material";
import {
  cloneElement,
  type FC,
  type MouseEvent,
  type ReactElement,
  useMemo,
  useState,
} from "react";

import { StyledTooltip } from "~/components/UI/StyledTooltip";
import { HELP_CENTER_LINK } from "~/constants/socials";

import * as Styled from "./RichTooltip.styles";

const LEARN_MORE_TEXT = "Learn more";

interface IRichTooltipProps {
  title?: string | number | null;
  textTitle?: string;
  children: ReactElement<{ onClick?: (event: MouseEvent<HTMLElement>) => void }>;
  actionText?: string;
  action?: () => void;
  link?: string;
  maxWidth?: number;
}

export const RichTooltip: FC<IRichTooltipProps> = ({
  title,
  textTitle,
  children,
  actionText = LEARN_MORE_TEXT,
  action,
  link,
  maxWidth = 240,
}) => {
  const isTouch = useMediaQuery("(pointer: coarse)");
  const [isOpen, setIsOpen] = useState(false);

  const actionNode = useMemo(() => {
    if (action) {
      return (
        <Styled.LearnMoreLink onClick={action} variant="caption">
          {actionText}
        </Styled.LearnMoreLink>
      );
    }

    return (
      <Styled.LearnMoreLink
        href={link || HELP_CENTER_LINK}
        target="_blank"
        rel="noopener noreferrer"
        variant="caption"
      >
        {actionText}
      </Styled.LearnMoreLink>
    );
  }, [action, actionText, link]);

  if (!title) return children;

  const content = (
    <Styled.TooltipContent>
      {textTitle && (
        <Typography variant="caption" sx={{
          fontWeight: 600
        }}>
          {textTitle}
        </Typography>
      )}
      <Typography variant="caption">{title}</Typography>
      {actionNode}
    </Styled.TooltipContent>
  );

  if (!isTouch) {
    return (
      <StyledTooltip
        title={content}
        maxWidth={maxWidth}
        arrow
        enterTouchDelay={0}
        leaveTouchDelay={2000}
      >
        {children}
      </StyledTooltip>
    );
  }

  const trigger = cloneElement(children, {
    onClick: (event: MouseEvent<HTMLElement>) => {
      children.props.onClick?.(event);
      setIsOpen((previous) => !previous);
    },
  });

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <StyledTooltip
        title={content}
        maxWidth={maxWidth}
        arrow
        open={isOpen}
        onClose={() => setIsOpen(false)}
        disableHoverListener
        disableTouchListener
        disableFocusListener
      >
        {trigger}
      </StyledTooltip>
    </ClickAwayListener>
  );
};
